import type { ComponentProps, FocusEvent, KeyboardEvent } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cva } from "class-variance-authority";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { useSelectContext } from "./SelectContext";

const selectTriggerButtonVariants = cva(
  "flex h-8 items-center justify-between gap-2 rounded-[10px] border px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20 data-[placeholder]:text-muted-foreground dark:aria-[invalid=true]:ring-destructive/40 [&>[data-slot=select-value]]:flex [&>[data-slot=select-value]]:items-center [&>[data-slot=select-value]]:gap-2 [&_span]:text-left [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
  {
    variants: {
      variant: {
        default: "border-input bg-background dark:bg-input/30 dark:hover:bg-input/50",
        // Keeps the 1px border box (transparent) so switching variants never shifts layout.
        inline: "border-transparent bg-transparent",
        // Same translucent hover as a muted ghost Button, so a trigger and a button side by side hover alike.
        ghost: "border-transparent bg-transparent hover:bg-foreground/5 dark:hover:bg-foreground/10",
      },
      width: {
        default: "[&_span]:w-full [&_span]:truncate",
        auto: "",
      },
    },
    defaultVariants: {
      variant: "default",
      width: "default",
    },
  }
);

export type SelectTriggerButtonProps = ComponentProps<"button"> & {
  variant?: "default" | "inline" | "ghost";
  width?: "default" | "auto";
  openOnFocus?: boolean;
  asChild?: boolean;
};

/** The trigger control itself; `Select.Trigger` places it (and, in multi mode, the badges under it). */
export const SelectTriggerButton = ({
  className,
  children,
  variant,
  width,
  openOnFocus = false,
  asChild = false,
  disabled,
  onFocus,
  onKeyDown,
  ...props
}: SelectTriggerButtonProps) => {
  const { selectedValues, setOpen, disabled: rootDisabled, suppressFocusOpen } = useSelectContext();
  const showsPlaceholder = selectedValues.length === 0;

  const handleFocus = (event: FocusEvent<HTMLButtonElement>) => {
    onFocus?.(event);
    if (suppressFocusOpen.current) {
      suppressFocusOpen.current = false;
      return;
    }
    if (openOnFocus) setOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    // Opening on keydown (as Radix Select does) also stops the button's native click from toggling it shut again.
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
    }
  };

  // The Popover trigger is itself a Slot, so a custom child receives the trigger wiring directly. It
  // keeps its native button role: a combobox can't be named by its content, and a custom trigger is
  // named by its own visible text.
  if (asChild) {
    return (
      <PopoverPrimitive.Trigger
        asChild
        aria-haspopup="listbox"
        data-slot="select-trigger"
        disabled={disabled || rootDisabled}
        className={className}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </PopoverPrimitive.Trigger>
    );
  }

  return (
    <PopoverPrimitive.Trigger asChild>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        data-slot="select-trigger"
        data-placeholder={showsPlaceholder ? "" : undefined}
        disabled={disabled || rootDisabled}
        className={cn(selectTriggerButtonVariants({ variant, width }), className)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
        <ChevronDownIcon className="size-4 opacity-50" />
      </button>
    </PopoverPrimitive.Trigger>
  );
};
